import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { MotionConfig } from "framer-motion";
import { languagePath, renderHead } from "@/site";
import NotFound from "@/pages/NotFound";
import { LanguageProvider } from "@/i18n/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import CernioPage from "@/pages/CernioPage";
import CompanyPage from "@/pages/CompanyPage";
import DosePage from "@/pages/DosePage";
import KampanyaPage from "@/pages/KampanyaPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";

function PageEffects() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    document.documentElement.lang = languagePath(pathname).lang;
    document
      .querySelectorAll("[data-site-meta]")
      .forEach((node) => node.remove());
    document.head.insertAdjacentHTML("beforeend", renderHead(pathname));
    const frame = requestAnimationFrame(() => {
      let target = null;
      try {
        target = hash
          ? document.getElementById(decodeURIComponent(hash.slice(1)))
          : null;
      } catch {
        /* Invalid fragments have no scroll target. */
      }
      if (target)
        target.scrollIntoView({ behavior: "instant", block: "start" });
      else if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let lenis, frame;
    const stop = () => {
      cancelAnimationFrame(frame);
      lenis?.destroy();
      lenis = null;
      window.__lenis = null;
    };
    const setup = () => {
      stop();
      if (preference.matches) return;
      lenis = new Lenis({ duration: 1.15, smoothWheel: true });
      window.__lenis = lenis;
      const loop = (time) => {
        lenis.raf(time);
        frame = requestAnimationFrame(loop);
      };
      frame = requestAnimationFrame(loop);
    };
    setup();
    preference.addEventListener("change", setup);
    return () => {
      preference.removeEventListener("change", setup);
      stop();
    };
  }, []);
  return null;
}
export function Site() {
  const location = useLocation();
  const { path } = languagePath(location.pathname);
  return (
    <LanguageProvider>
      <MotionConfig reducedMotion="user">
        <PageEffects />
        <div className="min-h-screen bg-paper text-ink">
          <Navbar key={location.pathname} />
          <main id="main-content">
            <Routes location={{ ...location, pathname: path }}>
              <Route path="/" element={<Home />} />
              <Route path="/products/cernio" element={<CernioPage />} />
              <Route
                path="/projects/dose-modernization"
                element={<DosePage />}
              />
              <Route
                path="/projects/kampanyambenim"
                element={<KampanyaPage />}
              />
              <Route path="/company" element={<CompanyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </MotionConfig>
    </LanguageProvider>
  );
}
export default function App() {
  return (
    <BrowserRouter>
      <Site />
    </BrowserRouter>
  );
}
